import {
  GetComponentsRequest,
  GetComponentsResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/components_pb";
import {
  GetSubscriptionComponentsRequest,
  GetSubscriptionComponentsResponse,
  UpdateSubscriptionComponentPriceRequest,
  UpdateSubscriptionComponentPriceResponse,
  UpdateSubscriptionComponentQuantityRequest,
  UpdateSubscriptionComponentQuantityResponse,
  UpdateSubscriptionComponentRequest,
  UpdateSubscriptionComponentResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import {
  Component,
  ComponentType,
  PriceTier,
  SubscriptionComponent,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import {
  ChargePolicy,
  ProrationPolicy,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

export const getBillingComponents = async (
  site: string,
  headers: Record<RequestMetadata, string>
): Promise<GetComponentsResponse> => {
  const req = new GetComponentsRequest({
    site,
  });

  return supportServiceConnectClient.getBillingComponents(req, {
    headers,
  });
};

type UpdateSubscriptionComponentParams = {
  subId: string;
  componentPricePointId: number;
  quantity: number;
  upgradeProrationPolicy?: ProrationPolicy;
  downgradeProrationPolicy?: ProrationPolicy;
  chargePolicy?: ChargePolicy;
  memo?: string;
  organizationId: string;
  componentType: ComponentType;
};

export const updateSubscriptionComponent = async (
  {
    subId,
    componentPricePointId,
    quantity,
    upgradeProrationPolicy = ProrationPolicy.UNSPECIFIED,
    downgradeProrationPolicy = ProrationPolicy.UNSPECIFIED,
    chargePolicy = ChargePolicy.UNSPECIFIED,
    memo = "Update subscription user seats quota",
    organizationId,
    componentType,
  }: UpdateSubscriptionComponentParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateSubscriptionComponentResponse> => {
  const req = new UpdateSubscriptionComponentRequest({
    subscriptionId: subId,
    componentPriceId: componentPricePointId,
    quantity,
    upgradeProrationPolicy,
    downgradeProrationPolicy,
    chargePolicy,
    memo,
    organizationId,
    componentType,
  });

  return supportServiceConnectClient.updateSubscriptionComponent(req, {
    headers,
  });
};

type UpdateSubscriptionComponentQuantityParams = {
  subId: string;
  componentId: number;
  quantity: number;
};

export const updateSubscriptionComponentQuantity = async (
  { subId, componentId, quantity }: UpdateSubscriptionComponentQuantityParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateSubscriptionComponentQuantityResponse> => {
  const req = new UpdateSubscriptionComponentQuantityRequest({
    subscriptionId: subId,
    componentId,
    quantity,
  });

  return supportServiceConnectClient.updateSubscriptionComponentQuantity(req, {
    headers,
  });
};

type UpdateSubscriptionComponentPriceParams = {
  subId: string;
  componentPriceId: number;
  organizationId: string;
};

export const updateSubscriptionComponentPrice = async (
  {
    subId,
    componentPriceId,
    organizationId,
  }: UpdateSubscriptionComponentPriceParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateSubscriptionComponentPriceResponse> => {
  const req = new UpdateSubscriptionComponentPriceRequest({
    subscriptionId: subId,
    componentPriceId,
    organizationId,
  });

  return supportServiceConnectClient.updateSubscriptionComponentPrice(req, {
    headers,
  });
};

type getSubscriptionComponentBySubIdParams = {
  subId: string;
};

export const getSubscriptionComponentBySubId = async (
  { subId }: getSubscriptionComponentBySubIdParams,
  headers: Record<RequestMetadata, string>
): Promise<GetSubscriptionComponentsResponse> => {
  const req = new GetSubscriptionComponentsRequest({
    subscriptionId: subId,
  });

  return supportServiceConnectClient.getSubscriptionComponents(req, {
    headers,
  });
};
type GetComponentsWithDefaultPriceParams = {
  componentTypes: ComponentType[];
  currency: string;
  period: number;
  site?: string;
};

export const getComponentsWithDefaultPrice = async (
  {
    componentTypes,
    currency,
    period,
    site,
  }: GetComponentsWithDefaultPriceParams,
  headers: Record<RequestMetadata, string>
): Promise<Component[]> => {
  const req = new GetComponentsRequest({ site });
  const result = await supportServiceConnectClient.getBillingComponents(req, {
    headers,
  });

  const components = result.components.filter((c) =>
    componentTypes.includes(c.componentType)
  );

  components.forEach((component) => {
    const prices = component.componentPrice.filter(
      (c) => c.currency === currency && c.period === period
    );

    if (prices.length > 0) {
      component.componentPrice = prices;
    }
  });

  return components;
};

type GetComponentWithDefaultLiteSeatBySubIdParams = {
  subId: string;
  currency: string;
  period: number;
  site?: string;
};

export const getSubComponentWithDefaultPriceBySubId = async (
  {
    subId,
    currency,
    period,
    site,
  }: GetComponentWithDefaultLiteSeatBySubIdParams,
  headers: Record<RequestMetadata, string>
): Promise<SubscriptionComponent[]> => {
  const req = new GetSubscriptionComponentsRequest({
    subscriptionId: subId,
  });

  return supportServiceConnectClient
    .getSubscriptionComponents(req, { headers })
    .then((resp: GetSubscriptionComponentsResponse) => {
      return resp.subscriptionComponents;
    })
    .then((components) => {
      let hasLite = false;
      let hasSensor = false;
      components.forEach((c) => {
        if (c.componentType === ComponentType.LITE_SEAT) {
          hasLite = true;
        }
      });
      components.forEach((c) => {
        if (c.componentType === ComponentType.SENSOR_UNIT) {
          hasSensor = true;
        }
      });

      if (hasLite && hasSensor) {
        return components;
      }

      return getComponentsWithDefaultPrice(
        {
          componentTypes: [ComponentType.LITE_SEAT, ComponentType.SENSOR_UNIT],
          currency,
          period,
          site,
        },
        headers
      ).then((cs) => {
        if (cs.length > 0) {
          cs.forEach((c) => {
            if (hasLite && c.componentType === ComponentType.LITE_SEAT) {
              return;
            }
            if (hasSensor && c.componentType === ComponentType.SENSOR_UNIT) {
              return;
            }
            components = components.concat(
              new SubscriptionComponent({
                id: c.id,
                sku: c.sku,
                name: c.name,
                period: c.period,
                quantity: 0,
                componentType: c.componentType,
                componentPrice: [
                  {
                    id: c.componentPrice[0].id,
                    currency: c.componentPrice[0].currency,
                    price: c.componentPrice[0].price,
                    componentId: c.componentPrice[0].componentId,
                    sku: c.componentPrice[0].sku,
                    period: c.componentPrice[0].period,
                    priceTier: PriceTier.PREMIUM,
                    name: c.componentPrice[0].name,
                  },
                ],
              })
            );
          });
        }

        return components;
      });
    });
};
